import { DropdownMenuItemType, IDropdownOption } from "@fluentui/react";
import { isEqual, isNil } from "lodash";
import * as React from "react";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { Floor, useFloorsQuery, useGetAvailableAmentiesBySpaceTypeQuery } from "@graphql";
import { FilterCacheContext } from "../../AppRoomBookings";
import { UserContext } from "../../Auth";
import BackHeader from "../shared/back-header/BackHeader";
import FilterDropDown from "./filter-dropdown/FilterDropDown";

export interface FilterCache {
  floor: IDropdownOption<Floor>;
  spaceType: IDropdownOption;
  amenities: string[];
}

interface Props {
  selectedLocationId: string;
  floors?: Floor[];
  onBackClick: () => void;
  onFilterChange: (floorId?: string, spaceType?: string, amenities?: string[]) => void;
}

const types: IDropdownOption[] = [
  { key: "no-type", text: "No Type" },
  { key: "common", text: "Common", itemType: DropdownMenuItemType.Header },
  { key: "meetingroom", text: "Meeting Room" },
  { key: "office", text: "Office" },
  { key: "breakout", text: "Breakout" },
  { key: "callroom", text: "Call Room" },
  { key: "classroom", text: "Class Room" },
  { key: "conferenceroom", text: "Conference Room" },
  { key: "event", text: "Event" },
  { key: "studyroom", text: "Study Room" },
  { key: "specialty", text: "Specialty", itemType: DropdownMenuItemType.Header },
  { key: "breakroom", text: "Break Room" },
  { key: "cafe", text: "Café" },
  { key: "cafeteria", text: "Cafeteria" },
  { key: "interviewroom", text: "Interview Room" },
  { key: "gym", text: "Gym" },
  { key: "kitchen", text: "Kitchen" },
  { key: "lab", text: "Lab" },
  { key: "auditorium", text: "Auditorium" },
];

export const amenities: IDropdownOption[] = [
  { key: "accessible", text: "Wheelchair Access" },
  { key: "wifi", text: "Wifi" },
  { key: "interactiveWhiteboard", text: "Interactive Whiteboard" },
  { key: "display", text: "Display" },
  { key: "deskPower", text: "Desk Power" },
  { key: "conferenceCall", text: "Conference Call" },
  { key: "serviceAvailable", text: "Service Available" },
  { key: "macCompatible", text: "Mac Compatible" },
  { key: "highSpecDesktopComputer", text: "High Spec Desktop Computer" },
  { key: "heightAdjustable", text: "Height Adjustable" },
  { key: "keyboard", text: "Keyboard" },
  { key: "mouse", text: "Mouse" },
  { key: "fixedPC", text: "Fixed PC" },
  { key: "accessibilitySeat", text: "Accessibility  Seat" },
  { key: "monitor", text: "Monitor" },
  { key: "desktopComputer", text: "Desktop Computer" },
  { key: "powerSource", text: "Power Source" },
  { key: "largeMonitor", text: "Large Monitor" },
  { key: "standingDesk", text: "Standing Desk" },
  { key: "dataCable", text: "Data Cable" },
  { key: "conferencePhone", text: "Conference Phone" },
  { key: "videoConferencing", text: "Video Conferencing" },
  { key: "laptopRiser", text: "Laptop Riser" },
  { key: "dualMonitors", text: "Dual Monitors" },
  { key: "iPad", text: "iPad" },
  { key: "highTable", text: "High Table" },
  { key: "bench", text: "Bench" },
  { key: "electricSpace", text: "Electric Space" },
  { key: "phone", text: "Phone" },
  { key: "power", text: "Power" },
  { key: "windowView", text: "Window View" },
  { key: "disabledSpace", text: "Disabled Space" },
  { key: "keyboardMouse", text: "Keyboard Mouse" },
  { key: "booth", text: "Booth" },
  { key: "heated", text: "Heated" },
  { key: "keyLock", text: "Key Lock" },
  { key: "wheelchairAccess", text: "Wheelchair Access" },
  { key: "combinationLock", text: "Combination Lock" },
  { key: "oversized", text: "Oversized" },
  { key: "hybridMeetingRoom", text: "Hybrid Meeting Room" },
  { key: "hdmiDataCable", text: "HDMI Data Cable" },
  { key: "largeDisplayScreen", text: "Large Display Screen" },
];

const Filters: FunctionComponent<Props> = (props: Props) => {
  const { onBackClick, floors: floorsData, selectedLocationId, onFilterChange } = props;
  const user = useContext(UserContext);
  const [floors, setFloors] = useState<IDropdownOption<Floor>[]>();
  const [selectedFloor, setSelectedFloor] = useState<IDropdownOption<Floor>>();
  const [selectedType, setSelectedType] = useState<IDropdownOption>({ key: "meetingroom", text: "Meeting Room" });
  const [filterCache, setFilterCache] = useContext(FilterCacheContext);
  const [selectedAmenities, setSelectedAmenities] = React.useState<string[]>([]);

  const { data: amenitiesData } = useGetAvailableAmentiesBySpaceTypeQuery({
    variables: { spaceType: selectedType ? (selectedType.key as string) : "meetingroom" },
  });

  useEffect(() => {
    if (isNil(selectedLocationId)) {
      return undefined;
    }
    if (floorsData) {
      setFloors(floorsData?.filter((f) => !f.deleted).map((f) => ({ key: f._id, text: f.name })));
      if (!isNil(filterCache) && !isNil(filterCache.floor) && !isEqual(filterCache.floor, selectedFloor)) {
        setSelectedFloor(filterCache.floor);
      }
      if (!isNil(filterCache) && !isNil(filterCache.spaceType) && !isEqual(filterCache.spaceType, selectedType)) {
        setSelectedType(filterCache.spaceType);
      }
      if (!isNil(filterCache) && !isNil(filterCache.amenities) && !isEqual(filterCache.amenities, selectedAmenities)) {
        setSelectedAmenities(filterCache.amenities);
      }
    }
  }, [
    floorsData,
    filterCache,
    selectedAmenities,
    selectedFloor,
    selectedLocationId,
    selectedType,
    amenitiesData,
    user,
  ]);

  const onFloorChange = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    setSelectedFloor(item);
    filterCache.floor = item;
    setFilterCache(filterCache);
    filterChange();
  };

  const onTypeChange = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    setSelectedType(item);
    filterCache.spaceType = item;
    setFilterCache(filterCache);
    filterChange();
  };

  const onAmenitiesChange = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      const items = item.selected
        ? [...selectedAmenities, item.key as string]
        : selectedAmenities.filter((key) => key !== item.key);
      setSelectedAmenities(items);
      filterCache.amenities = items;
      setFilterCache(filterCache);
      filterChange();
    }
  };

  const filterChange = () => {
    onFilterChange(filterCache.floor?.key as string, filterCache.spaceType?.key as string, filterCache.amenities);
  };

  const onClearType = () => {
    setSelectedType(undefined);
    filterCache.spaceType = undefined;
    setFilterCache(filterCache);
    filterChange();
  };

  const onClearFloor = () => {
    setSelectedFloor(undefined);
    filterCache.floor = undefined;
    setFilterCache(filterCache);
    filterChange();
  };

  const onClearAmenities = () => {
    setSelectedAmenities(undefined);
    filterCache.amenities = undefined;
    setFilterCache(filterCache);
    filterChange();
  };

  return (
    <>
      <BackHeader title={"Filters"} onBackClick={onBackClick} />
      <FilterDropDown
        selectedKey={selectedType ? selectedType.key : undefined}
        onChange={onTypeChange}
        placeholder="Space Type"
        options={types}
        onClearClick={onClearType}
      />
      <FilterDropDown
        selectedKey={selectedFloor ? selectedFloor.key : undefined}
        onChange={onFloorChange}
        placeholder="Floor"
        options={floors}
        onClearClick={onClearFloor}
      />
      <FilterDropDown
        placeholder="Amenities"
        selectedKeys={selectedAmenities}
        onChange={onAmenitiesChange}
        multiSelect={true}
        options={amenities}
        onClearClick={onClearAmenities}
      />
    </>
  );
};

export default Filters;
